import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type PointerEvent as PointerEventDoReact,
} from "react";
import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  ArrowLineDown,
  ArrowLineUp,
  ArrowUUpLeft,
  ArrowUUpRight,
  Copy,
  Minus,
  Plus,
  Sticker,
  TextT,
} from "@phosphor-icons/react";
import {
  AREA_UTIL,
  MAX_CAMADAS_POR_CELULA,
  cantosDaCaixa,
  estiloDaImagem,
  type AreaNaCelula,
  type Camada,
  type CaixaDaCamada,
  type Enquadramento,
  type FigurinhaDaBiblioteca,
  type Ponto,
  type Tamanho,
} from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { Botao, Falha, cx } from "../../components/ui.js";
import { CamadasNaCelula } from "./CamadasNaCelula.js";
import { InspetorDaCamada } from "./InspetorDaCamada.js";
import { PilhaDeCamadas } from "./PilhaDeCamadas.js";
import { SeletorDeFigurinhas } from "./SeletorDeFigurinhas.js";
import {
  camadaDeImagemNova,
  camadaDeTextoNova,
  comandoDoTeclado,
  deslocarCamada,
  escalarCamada,
  girarCamada,
  semTextosEmBranco,
} from "./camadas-da-celula.js";
import { encaixarCelula, itensDaCelula } from "./caixas.js";
import {
  camadasIguais,
  estadoInicial,
  podeDesfazer,
  podeRefazer,
  reducerDaEdicao,
} from "./edicao.js";
import { camadasIndisponiveis, idsDasFigurinhas } from "./figurinhas-das-camadas.js";
import {
  andouDemais,
  aplicarGesto,
  cursorNoPonto,
  iniciarGesto,
  iniciarPinca,
  posicoesDasAlcas,
  type Gesto,
  type ItemDaCelula,
} from "./gestos.js";
import { motivoDeNaoCaber, totaisDasCamadas } from "./limites-da-folha.js";
import { useFigurinhasDasCamadas } from "./useFigurinhasDasCamadas.js";

/** Medida em milímetros como se escreve em português: "93,6", não "93.6". */
const emMm = (mm: number): string => String(mm).replace(".", ",");

/**
 * O espaço, em px, que a célula deixa livre em volta dela na área do editor: as alças saem do canto da
 * caixa (e a de girar fica acima dela), e uma camada junto da borda tem alça fora da célula.
 */
const MARGEM_DA_CELULA = 30;

/** O que "Aplicar em todas as fotos" devolve à tela depois de feito. */
export interface ResultadoDaAplicacao {
  /** Quantas fotos receberam as camadas. */
  atualizadas: number;
  /** As fotos (pelo nome) que passariam do limite de camadas e ficaram como estavam. */
  naoCouberam: string[];
  /** Devolve a folha ao que era antes de aplicar. */
  desfazer: () => void;
}

/** "Aplicar em todas" que não se fez: o motivo, em português (a folha ficaria acima de um teto do servidor). */
export interface AplicacaoRecusada {
  recusada: string;
}

type GestoAtivo = Gesto & { chave: string; ids: number[] };

/**
 * O editor de camadas: figurinhas, logos e textos que o operador arrasta sobre a foto de uma célula da folha.
 *
 * A célula aparece **na proporção real**, com a foto já enquadrada (a mesma conta do enquadramento) e as camadas
 * por cima, na ordem do array — o fim é a frente. Toda a geometria (caixa, clique, arrastar, esticar, girar,
 * pinça) vem de `@foto/shared`, a mesma que o servidor executa no JPEG: aqui só se traduz o ponteiro.
 *
 * O trabalho é um **rascunho**: nada vai para a folha até "Concluir". O desfazer guarda as últimas 30 edições,
 * e um gesto inteiro (arrastar, esticar, girar) é uma edição só (`edicao.ts`).
 *
 * **Ponteiro.** Pointer Events com `setPointerCapture` e `touch-action: none` só na área da célula (a página em
 * volta continua rolando). Com dois dedos sobre uma camada selecionada, a pinça escala, gira e leva a camada.
 * A cada gesto, as contas partem da camada do **início** dele.
 */
export function EditorDeCamadas({
  fotoUrl,
  foto,
  celula,
  areaUtil,
  enquadramento,
  camadas: camadasIniciais,
  fotosNaFolha,
  camadasDasOutras,
  aoSalvar,
  aoCancelar,
  aoAplicarEmTodas,
}: {
  fotoUrl: string;
  /** O tamanho da foto: só a proporção importa. */
  foto: Tamanho;
  /** O tamanho da célula, em qualquer unidade: só a proporção importa. */
  celula: Tamanho;
  /** O que o papel imprime e mantém dentro da célula, ou nulo quando ela cabe inteira nele. */
  areaUtil?: AreaNaCelula | null;
  enquadramento: Enquadramento;
  camadas: readonly Camada[];
  /** Quantas OUTRAS células da folha têm foto (para onde "Aplicar em todas" levaria as camadas). */
  fotosNaFolha: number;
  /**
   * As camadas das OUTRAS fotos da folha. O servidor limita a folha inteira (60 camadas, 24 figurinhas diferentes),
   * e o editor só sabe dizer que uma camada nova não cabe se conhece o que as outras fotos já têm.
   */
  camadasDasOutras: readonly Camada[];
  aoSalvar: (camadas: Camada[]) => void;
  aoCancelar: () => void;
  /** Aplica as camadas nas outras fotos da folha. Sem isto, o editor não oferece o botão. */
  aoAplicarEmTodas?: (
    camadas: Camada[],
    modo: "substituir" | "acrescentar",
  ) => ResultadoDaAplicacao | AplicacaoRecusada;
}) {
  const utils = trpc.useUtils();
  const [estado, dispatch] = useReducer(reducerDaEdicao, camadasIniciais, estadoInicial);
  const estadoRef = useRef(estado);
  estadoRef.current = estado;

  // O que está guardado na folha para esta célula: o que o rascunho é comparado para saber se há mudança.
  const [guardado, setGuardado] = useState<readonly Camada[]>(camadasIniciais);
  const alterado = !camadasIguais(estado.camadas, guardado);
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);

  const [seletorAberto, setSeletorAberto] = useState(false);
  const [aplicacao, setAplicacao] = useState<
    (ResultadoDaAplicacao & { guardadoAntes: readonly Camada[] }) | null
  >(null);

  // As figurinhas do rascunho, que ainda não estão na folha: o editor as pergunta à biblioteca por conta própria.
  const idsDasImagens = useMemo(() => idsDasFigurinhas(estado.camadas), [estado.camadas]);
  const figurinhas = useFigurinhasDasCamadas(idsDasImagens);
  const indisponiveis = camadasIndisponiveis(estado.camadas, figurinhas);

  // ─── A área e a célula ──────────────────────────────────────────────────────

  const areaRef = useRef<HTMLDivElement>(null);
  const celulaRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState<Tamanho | null>(null);
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const medir = () => setArea({ largura: el.clientWidth, altura: el.clientHeight });
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  const proporcao = celula.largura / celula.altura;
  const celulaPx = useMemo(
    () => (area ? encaixarCelula(area, proporcao, MARGEM_DA_CELULA) : null),
    [area, proporcao],
  );
  const celulaPxRef = useRef(celulaPx);
  celulaPxRef.current = celulaPx;

  // O tamanho de cada bloco de texto, medido no DOM (ver `CamadasNaCelula`).
  const [blocos, setBlocos] = useState<Record<string, Tamanho>>({});
  const aoMedirTexto = useCallback((id: string, bloco: Tamanho) => {
    setBlocos((atual) => {
      const antes = atual[id];
      return antes && antes.largura === bloco.largura && antes.altura === bloco.altura
        ? atual
        : { ...atual, [id]: bloco };
    });
  }, []);

  const itens = useMemo(
    () => (celulaPx ? itensDaCelula(estado.camadas, figurinhas, blocos, celulaPx) : []),
    [estado.camadas, figurinhas, blocos, celulaPx],
  );
  const itensRef = useRef<ItemDaCelula[]>(itens);
  itensRef.current = itens;

  const selecionada = estado.camadas.find((c) => c.id === estado.selecionada) ?? null;
  const agirNaSelecionada = (fazer: (camada: Camada) => void) => {
    if (selecionada) fazer(selecionada);
  };
  const itemSelecionado = itens.find((i) => i.id === estado.selecionada) ?? null;

  // ─── O ponteiro ─────────────────────────────────────────────────────────────

  const ponteiros = useRef(new Map<number, Ponto>());
  const gesto = useRef<GestoAtivo | null>(null);
  const quadro = useRef<number | null>(null);
  const teclas = useRef({ shift: false, alt: false });
  const contadorDeGestos = useRef(0);
  const [cursor, setCursor] = useState<string>("default");

  /** O ponto da tela em pixels da célula (pode cair fora dela: as alças saem da célula). */
  function pontoDaCelula(e: { clientX: number; clientY: number }): Ponto {
    const r = celulaRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  const novaChaveDeGesto = () => `gesto:${++contadorDeGestos.current}`;

  /** Aplica o gesto com o ponteiro onde está agora. Uma vez por quadro: o ponteiro dispara bem mais que isso. */
  function aplicarOGesto() {
    quadro.current = null;
    const g = gesto.current;
    const tamanho = celulaPxRef.current;
    if (!g || !tamanho) return;

    const pontos = g.ids
      .map((id) => ponteiros.current.get(id))
      .filter((p): p is Ponto => p !== undefined);
    if (g.tipo === "fundo") {
      const p = pontos[0];
      if (p && andouDemais(g.inicio, p)) g.moveu = true;
      return;
    }
    const camada = aplicarGesto(g, pontos, tamanho, teclas.current);
    if (camada) dispatch({ tipo: "alterar", camada, chave: g.chave });
  }

  function agendarQuadro() {
    if (quadro.current === null) quadro.current = requestAnimationFrame(aplicarOGesto);
  }

  function aoPressionar(e: PointerEventDoReact<HTMLDivElement>) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (!celulaPxRef.current) return;
    // O mouse é um ponteiro só: o que sobrou de um gesto cujo "soltar" não chegou não vira uma pinça.
    if (e.pointerType === "mouse") ponteiros.current.clear();
    e.currentTarget.setPointerCapture(e.pointerId);
    // Deixa o teclado valer (setas, Delete): o clique na área passa o foco para ela, tirando-o de um campo.
    e.currentTarget.focus({ preventScroll: true });

    const p = pontoDaCelula(e);
    ponteiros.current.set(e.pointerId, p);
    teclas.current = { shift: e.shiftKey, alt: e.altKey };

    const atual = estadoRef.current;
    const item = itensRef.current.find((i) => i.id === atual.selecionada);

    // O segundo dedo sobre uma camada selecionada: pinça. Vale mesmo se o primeiro caiu no vazio.
    if (ponteiros.current.size === 2 && item) {
      const ids = [...ponteiros.current.keys()];
      const [a, b] = ids.map((id) => ponteiros.current.get(id)!) as [Ponto, Ponto];
      aplicarOGesto();
      gesto.current = {
        ...iniciarPinca(
          itensRef.current.find((i) => i.id === atual.selecionada)!,
          [a, b],
        ),
        chave: novaChaveDeGesto(),
        ids,
      };
      dispatch({ tipo: "fechar-agrupamento" });
      return;
    }
    if (ponteiros.current.size > 1) return;

    const g = iniciarGesto(p, itensRef.current, atual.selecionada);
    if (g.tipo !== "fundo" && g.id !== atual.selecionada)
      dispatch({ tipo: "selecionar", id: g.id });
    gesto.current = { ...g, chave: novaChaveDeGesto(), ids: [e.pointerId] };
  }

  function aoMover(e: PointerEventDoReact<HTMLDivElement>) {
    if (!celulaPxRef.current) return;
    teclas.current = { shift: e.shiftKey, alt: e.altKey };

    if (ponteiros.current.has(e.pointerId)) {
      ponteiros.current.set(e.pointerId, pontoDaCelula(e));
      if (gesto.current) agendarQuadro();
      return;
    }
    // Sem gesto: o mouse passando sobre a área mostra o que o clique faria.
    if (e.pointerType === "mouse" && !gesto.current) {
      const proximo = cursorNoPonto(
        pontoDaCelula(e),
        itensRef.current,
        estadoRef.current.selecionada,
      );
      setCursor((c) => (c === proximo ? c : proximo));
    }
  }

  function aoSoltar(e: PointerEventDoReact<HTMLDivElement>) {
    if (!ponteiros.current.has(e.pointerId)) return;
    // O último movimento chega antes de soltar: sem isto o gesto perderia o fim do arrasto.
    ponteiros.current.set(e.pointerId, pontoDaCelula(e));
    if (quadro.current !== null) cancelAnimationFrame(quadro.current);
    aplicarOGesto();
    ponteiros.current.delete(e.pointerId);

    const g = gesto.current;
    if (!g) return;

    // Um dedo saiu da pinça e o outro continua: a camada segue o que ficou, sem pular.
    if (g.tipo === "pinca" && ponteiros.current.size === 1) {
      const [id, ponto] = [...ponteiros.current.entries()][0]!;
      const camada = estadoRef.current.camadas.find((c) => c.id === g.id);
      if (camada) {
        gesto.current = {
          tipo: "mover",
          id: g.id,
          inicial: camada,
          inicio: ponto,
          chave: novaChaveDeGesto(),
          ids: [id],
        };
        dispatch({ tipo: "fechar-agrupamento" });
        return;
      }
    }
    if (ponteiros.current.size > 0) return;

    // Um toque no vazio, sem andar, tira a seleção.
    if (g.tipo === "fundo" && !g.moveu && e.type === "pointerup")
      dispatch({ tipo: "selecionar", id: null });
    gesto.current = null;
    dispatch({ tipo: "fechar-agrupamento" });
  }

  // O quadro agendado não pode sobreviver ao editor.
  useEffect(
    () => () => {
      if (quadro.current !== null) cancelAnimationFrame(quadro.current);
    },
    [],
  );

  // ─── O teclado ──────────────────────────────────────────────────────────────

  // Mais uma camada cabe? Na foto (12), na folha (60) e, para uma figurinha nova, nas 24 diferentes.
  const motivoDeNaoCaberMais = motivoDeNaoCaber({
    daCelula: estado.camadas,
    outras: camadasDasOutras,
  });
  const cheia = motivoDeNaoCaberMais !== null;
  const motivoRef = useRef(motivoDeNaoCaberMais);
  motivoRef.current = motivoDeNaoCaberMais;
  const totaisDaFolha = totaisDasCamadas([...camadasDasOutras, ...estado.camadas]);

  const raizRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      const raiz = raizRef.current;
      // O seletor aberto por cima torna o editor inerte: as teclas são dele.
      if (!raiz || raiz.closest("[inert]")) return;
      const alvo = e.target as HTMLElement | null;
      // Dentro de um campo, a tecla é do campo (setas movem o cursor, Delete apaga letra, o controle
      // deslizante usa as setas): o editor não a toma.
      if (alvo?.closest("input, textarea, select, [contenteditable='true']")) return;

      const comando = comandoDoTeclado(e);
      if (!comando) return;
      const atual = estadoRef.current;
      const camada = atual.camadas.find((c) => c.id === atual.selecionada);

      switch (comando.tipo) {
        case "desfazer":
          e.preventDefault();
          dispatch({ tipo: "desfazer" });
          return;
        case "refazer":
          e.preventDefault();
          dispatch({ tipo: "refazer" });
          return;
        default:
      }
      if (!camada) return;
      e.preventDefault();
      switch (comando.tipo) {
        case "deslocar":
          dispatch({
            tipo: "alterar",
            camada: deslocarCamada(camada, comando.dx, comando.dy),
            chave: `tecla:${camada.id}`,
          });
          break;
        case "escalar":
          dispatch({
            tipo: "alterar",
            camada: escalarCamada(camada, comando.fator),
            chave: `tecla:${camada.id}`,
          });
          break;
        case "apagar":
          dispatch({ tipo: "apagar", id: camada.id });
          break;
        case "duplicar":
          // Duplicar é mais uma camada: vale o mesmo teto do botão.
          if (motivoRef.current === null) dispatch({ tipo: "duplicar", id: camada.id });
          break;
      }
    };
    // Cada toque de tecla é uma entrada do desfazer; segurar a seta é uma só.
    const aoSoltarATecla = () => dispatch({ tipo: "fechar-agrupamento" });

    document.addEventListener("keydown", aoTeclar);
    document.addEventListener("keyup", aoSoltarATecla);
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.removeEventListener("keyup", aoSoltarATecla);
    };
  }, []);

  // ─── Adicionar ──────────────────────────────────────────────────────────────

  const painelRef = useRef<HTMLDivElement>(null);
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0);
  // No celular o painel começa recolhido: a foto é o assunto, e quanto maior ela fica, mais fácil acertar o
  // dedo. Abre sozinho quando o operador pede um texto (o campo precisa aparecer) e a qualquer momento por um toque.
  const [painelAberto, setPainelAberto] = useState(false);
  useEffect(() => {
    if (pedidoDeFoco === 0) return;
    const campo = painelRef.current?.querySelector("textarea");
    if (!campo) return;
    campo.focus();
    // O texto de partida vem selecionado: digitar já o troca.
    campo.select();
    campo.scrollIntoView({ block: "nearest" });
  }, [pedidoDeFoco]);

  function adicionarImagem(figurinha: FigurinhaDaBiblioteca) {
    // A resposta já está em mãos: a camada nasce sem esperar a consulta de volta.
    utils.figurinhas.get.setData({ id: figurinha.id }, figurinha);
    dispatch({
      tipo: "adicionar",
      camada: camadaDeImagemNova(figurinha, estadoRef.current.camadas, celula),
    });
    setSeletorAberto(false);
  }

  function adicionarTexto() {
    dispatch({ tipo: "adicionar", camada: camadaDeTextoNova(estadoRef.current.camadas) });
    setPainelAberto(true);
    setPedidoDeFoco((n) => n + 1);
  }

  // ─── Concluir, descartar, aplicar em todas ──────────────────────────────────

  function pedirParaFechar() {
    if (alterado) setConfirmandoDescarte(true);
    else aoCancelar();
  }

  function concluir() {
    aoSalvar(semTextosEmBranco(estado.camadas));
  }

  const [escolhendoAplicacao, setEscolhendoAplicacao] = useState(false);
  const [recusaDaAplicacao, setRecusaDaAplicacao] = useState<string | null>(null);
  // Qualquer edição depois de aplicar deixa o aviso para trás: ele falava do que havia antes dela.
  useEffect(() => {
    setAplicacao(null);
    setRecusaDaAplicacao(null);
  }, [estado.camadas]);
  function aplicarEmTodas(modo: "substituir" | "acrescentar") {
    if (!aoAplicarEmTodas) return;
    const camadas = semTextosEmBranco(estado.camadas);
    const resultado = aoAplicarEmTodas(camadas, modo);
    // A folha passaria de um teto do servidor: nada foi aplicado, e a tela diz por quê.
    if ("recusada" in resultado) {
      setRecusaDaAplicacao(resultado.recusada);
      return;
    }
    setRecusaDaAplicacao(null);
    setEscolhendoAplicacao(false);
    // A célula passou a ter, na folha, o que o rascunho tem: não há mais "mudança" a descartar.
    const guardadoAntes = guardado;
    setGuardado(estado.camadas);
    // Depois do `setGuardado`: a mudança de `camadas` abaixo só ocorre com uma edição, e a aplicação não é uma.
    setAplicacao({ ...resultado, guardadoAntes });
  }

  function desfazerAplicacao() {
    if (!aplicacao) return;
    aplicacao.desfazer();
    setGuardado(aplicacao.guardadoAntes);
    setAplicacao(null);
  }

  // ─── O desenho ──────────────────────────────────────────────────────────────

  const estiloDaFoto = celulaPx ? estiloDaImagem(enquadramento, foto, celulaPx) : null;
  const deslocamento =
    area && celulaPx
      ? { left: (area.largura - celulaPx.largura) / 2, top: (area.altura - celulaPx.altura) / 2 }
      : null;

  return (
    <Sobreposicao rotulo="Figurinhas e textos" aoFechar={pedirParaFechar} tamanho="tela">
      <div ref={raizRef} className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* ─── A célula ─────────────────────────────────────────────────────── */}
        <section aria-label="A foto" className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex items-center gap-1 border-b border-borda px-2 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-4">
            <Botao tom="fantasma" formato="nu" className="min-h-11 px-3" onClick={pedirParaFechar}>
              Cancelar
            </Botao>
            <span className="flex-1 sm:hidden" aria-hidden />
            <h2 className="hidden min-w-0 flex-1 truncate text-center font-display text-[1.125rem] leading-tight sm:block">
              Figurinhas e textos
            </h2>
            <BotaoDeIcone
              rotulo="Desfazer"
              dica="Desfazer (Ctrl+Z)"
              desabilitado={!podeDesfazer(estado)}
              aoClicar={() => dispatch({ tipo: "desfazer" })}
            >
              <ArrowUUpLeft size={20} aria-hidden />
            </BotaoDeIcone>
            <BotaoDeIcone
              rotulo="Refazer"
              dica="Refazer (Ctrl+Shift+Z)"
              desabilitado={!podeRefazer(estado)}
              aoClicar={() => dispatch({ tipo: "refazer" })}
            >
              <ArrowUUpRight size={20} aria-hidden />
            </BotaoDeIcone>
            <Botao tom="brasa" className="min-h-11" onClick={concluir}>
              Concluir
            </Botao>
          </header>

          {confirmandoDescarte && (
            <div
              role="alertdialog"
              aria-label="Descartar as mudanças?"
              className="flex flex-wrap items-center gap-2 border-b border-borda bg-superficie px-4 py-2"
            >
              <p className="min-w-0 flex-1 text-[0.875rem] text-texto">
                Descartar as mudanças desta foto?
              </p>
              <Botao tom="descartar" className="min-h-11" onClick={aoCancelar}>
                Descartar
              </Botao>
              <Botao
                tom="quieto"
                className="min-h-11"
                onClick={() => setConfirmandoDescarte(false)}
              >
                Continuar editando
              </Botao>
            </div>
          )}

          <div
            ref={areaRef}
            tabIndex={0}
            role="application"
            aria-label="A foto, com as figurinhas e os textos. Arraste uma camada para movê-la. Setas movem, mais e menos mudam o tamanho, Delete apaga."
            onPointerDown={aoPressionar}
            onPointerMove={aoMover}
            onPointerUp={aoSoltar}
            onPointerCancel={aoSoltar}
            onDoubleClick={() => {
              if (selecionada?.tipo !== "texto") return;
              setPainelAberto(true);
              setPedidoDeFoco((n) => n + 1);
            }}
            style={{ cursor }}
            className="relative min-h-0 flex-1 touch-none select-none overflow-hidden bg-superficie/40 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brasa"
          >
            {celulaPx && deslocamento && estiloDaFoto && (
              <div
                ref={celulaRef}
                className="absolute"
                style={{
                  left: deslocamento.left,
                  top: deslocamento.top,
                  width: celulaPx.largura,
                  height: celulaPx.altura,
                }}
              >
                {/* A célula recorta o que passa da borda, como o papel. */}
                <div className="absolute inset-0 overflow-hidden bg-white shadow-lg">
                  <img
                    src={fotoUrl}
                    alt=""
                    draggable={false}
                    className="pointer-events-none absolute max-w-none select-none"
                    style={{
                      left: `${estiloDaFoto.left}%`,
                      top: `${estiloDaFoto.top}%`,
                      width: `${estiloDaFoto.width}%`,
                      height: `${estiloDaFoto.height}%`,
                    }}
                  />
                  <CamadasNaCelula
                    camadas={estado.camadas}
                    figurinhas={figurinhas}
                    celula={celulaPx}
                    aoMedirTexto={aoMedirTexto}
                    modo="editor"
                  />
                  {areaUtil && (
                    // A mesma linha do enquadramento: o que passa dela cai no picotado.
                    <span
                      aria-hidden
                      data-area-util
                      className="pointer-events-none absolute z-10 border border-dashed border-sky-400"
                      style={{
                        left: `${areaUtil.x * 100}%`,
                        top: `${areaUtil.y * 100}%`,
                        width: `${areaUtil.largura * 100}%`,
                        height: `${areaUtil.altura * 100}%`,
                        boxShadow: "0 0 0 100vmax rgb(0 0 0 / 0.34)",
                      }}
                    />
                  )}
                </div>

                {itemSelecionado && (
                  <CaixaDeSelecao caixa={itemSelecionado.caixa} celula={celulaPx} />
                )}
              </div>
            )}
          </div>

          <p className="hidden border-t border-borda px-4 py-2 text-[0.75rem] text-texto-fraco lg:block">
            Arraste para mover · cantos esticam (Alt: a partir do centro) · alça de cima gira
            (Shift: de 15° em 15°) · setas movem · + e − mudam o tamanho · Delete apaga · Ctrl+D
            duplica · Ctrl+Z desfaz
          </p>
        </section>

        {/* ─── O painel ─────────────────────────────────────────────────────── */}
        <aside
          ref={painelRef}
          aria-label="Camadas e ajustes"
          className={cx(
            "flex min-h-0 shrink-0 flex-col border-t border-borda bg-fundo pb-[env(safe-area-inset-bottom)]",
            "lg:max-h-none lg:w-[24rem] lg:border-l lg:border-t-0",
            painelAberto ? "max-h-[46dvh]" : "max-h-none",
          )}
        >
          <div className="grid shrink-0 grid-cols-2 gap-2 p-3">
            <Botao
              tom="quieto"
              className="min-h-11"
              disabled={cheia}
              onClick={() => setSeletorAberto(true)}
            >
              <Sticker size={18} aria-hidden />
              Figurinha ou logo
            </Botao>
            <Botao tom="quieto" className="min-h-11" disabled={cheia} onClick={adicionarTexto}>
              <TextT size={18} aria-hidden />
              Título ou frase
            </Botao>
            {motivoDeNaoCaberMais && (
              <p
                role="status"
                className="col-span-2 text-[0.8125rem] leading-relaxed text-descartar"
              >
                {motivoDeNaoCaberMais}
              </p>
            )}
          </div>

          {/* A barra fica sempre (desabilitada sem seleção): se ela aparecesse e sumisse, a foto pularia de tamanho a cada toque. */}
          <div
            role="toolbar"
            aria-label="Camada selecionada"
            className="grid shrink-0 grid-cols-6 gap-1 border-y border-borda px-2 py-1.5"
          >
            <Ferramenta
              rotulo="Frente"
              desabilitado={!selecionada}
              aoClicar={() =>
                agirNaSelecionada((c) => dispatch({ tipo: "ordem", id: c.id, para: "frente" }))
              }
            >
              <ArrowLineUp size={20} aria-hidden />
            </Ferramenta>
            <Ferramenta
              rotulo="Trás"
              desabilitado={!selecionada}
              aoClicar={() =>
                agirNaSelecionada((c) => dispatch({ tipo: "ordem", id: c.id, para: "tras" }))
              }
            >
              <ArrowLineDown size={20} aria-hidden />
            </Ferramenta>
            <Ferramenta
              rotulo="Duplicar"
              desabilitado={!selecionada || cheia}
              aoClicar={() => agirNaSelecionada((c) => dispatch({ tipo: "duplicar", id: c.id }))}
            >
              <Copy size={20} aria-hidden />
            </Ferramenta>
            <Ferramenta
              rotulo="Girar 90°"
              desabilitado={!selecionada}
              aoClicar={() =>
                agirNaSelecionada((c) => dispatch({ tipo: "alterar", camada: girarCamada(c, 90) }))
              }
            >
              <ArrowClockwise size={20} aria-hidden />
            </Ferramenta>
            <Ferramenta
              rotulo="Menor"
              desabilitado={!selecionada}
              aoClicar={() =>
                agirNaSelecionada((c) =>
                  dispatch({ tipo: "alterar", camada: escalarCamada(c, 1 / 1.1) }),
                )
              }
            >
              <Minus size={20} aria-hidden />
            </Ferramenta>
            <Ferramenta
              rotulo="Maior"
              desabilitado={!selecionada}
              aoClicar={() =>
                agirNaSelecionada((c) =>
                  dispatch({ tipo: "alterar", camada: escalarCamada(c, 1.1) }),
                )
              }
            >
              <Plus size={20} aria-hidden />
            </Ferramenta>
          </div>

          {indisponiveis.length > 0 && (
            <div className="shrink-0 px-3 pb-2">
              <Falha
                mensagem={
                  indisponiveis.length === 1
                    ? "Uma imagem desta foto não existe mais (foi apagada ou deixou de ser pública). Remova-a: enquanto ela estiver na folha, o pedido falha."
                    : `${indisponiveis.length} imagens desta foto não existem mais (foram apagadas ou deixaram de ser públicas). Remova-as: enquanto estiverem na folha, o pedido falha.`
                }
              />
            </div>
          )}

          <button
            type="button"
            aria-expanded={painelAberto}
            aria-controls="ajustes-das-camadas"
            onClick={() => setPainelAberto((a) => !a)}
            className="flex min-h-11 shrink-0 items-center justify-between gap-3 border-t border-borda px-3 text-left text-[0.9375rem] font-medium text-texto lg:hidden"
          >
            <span>
              Ajustes e camadas{" "}
              <span className="font-normal text-texto-suave">
                ({estado.camadas.length} de {MAX_CAMADAS_POR_CELULA})
              </span>
            </span>
            {painelAberto ? <CaretDown size={18} aria-hidden /> : <CaretUp size={18} aria-hidden />}
          </button>

          <div
            id="ajustes-das-camadas"
            className={cx(
              "min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain p-3",
              painelAberto ? "flex" : "hidden lg:flex",
            )}
          >
            {selecionada ? (
              <InspetorDaCamada
                key={selecionada.id}
                camada={selecionada}
                aoAlterar={(camada, chave) =>
                  dispatch({ tipo: "alterar", camada, ...(chave ? { chave } : {}) })
                }
                aoSoltar={() => dispatch({ tipo: "fechar-agrupamento" })}
              />
            ) : (
              estado.camadas.length > 0 && (
                <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
                  Toque numa camada, na foto ou na lista, para ajustá-la. Toque fora para tirar a
                  seleção.
                </p>
              )
            )}

            <PilhaDeCamadas
              camadas={estado.camadas}
              figurinhas={figurinhas}
              selecionada={estado.selecionada}
              aoSelecionar={(id) => dispatch({ tipo: "selecionar", id })}
              aoMoverNaOrdem={(id, para) => dispatch({ tipo: "ordem", id, para })}
              aoDuplicar={(id) => dispatch({ tipo: "duplicar", id })}
              aoApagar={(id) => dispatch({ tipo: "apagar", id })}
              duplicarDesabilitado={cheia}
              totaisDaFolha={totaisDaFolha}
            />

            {aoAplicarEmTodas && (
              <AplicarEmTodas
                fotosNaFolha={fotosNaFolha}
                semCamadas={estado.camadas.length === 0}
                escolhendo={escolhendoAplicacao}
                aoEscolher={setEscolhendoAplicacao}
                aoAplicar={aplicarEmTodas}
                resultado={aplicacao}
                recusada={recusaDaAplicacao}
                aoDesfazer={desfazerAplicacao}
              />
            )}

            <p className="text-[0.75rem] leading-relaxed text-texto-fraco">
              A linha azul tracejada, quando aparece, é a área que o papel imprime (
              {emMm(AREA_UTIL.larguraMm)} × {emMm(AREA_UTIL.alturaMm)} mm); o que passa dela cai no
              picotado.
            </p>
          </div>
        </aside>
      </div>

      {seletorAberto && (
        <SeletorDeFigurinhas
          aoEscolher={adicionarImagem}
          aoFechar={() => setSeletorAberto(false)}
          // A foto ou a folha cheia recusa tudo; as 24 figurinhas diferentes recusam só as que a folha ainda não usa.
          recusa={(f) =>
            motivoDeNaoCaber({
              daCelula: estado.camadas,
              outras: camadasDasOutras,
              nova: { tipo: "imagem", figurinhaId: f.id },
            })
          }
        />
      )}
    </Sobreposicao>
  );
}

// ─── Peças ────────────────────────────────────────────────────────────────────

/** A caixa da camada selecionada com as alças: de canto (esticam), e a de girar. Só desenho — o toque é de `gestos.ts`. */
function CaixaDeSelecao({ caixa, celula }: { caixa: CaixaDaCamada; celula: Tamanho }) {
  const cantos = cantosDaCaixa(caixa);
  const pontos = cantos.map((p) => `${p.x},${p.y}`).join(" ");
  const pos = posicoesDasAlcas(caixa);
  const lado = 14;

  return (
    <svg
      aria-hidden
      width={celula.largura}
      height={celula.altura}
      className="pointer-events-none absolute left-0 top-0 overflow-visible"
    >
      {/* Duas linhas, a escura por baixo: a caixa lê sobre foto clara e sobre foto escura. */}
      <polygon
        points={pontos}
        fill="none"
        stroke="rgb(0 0 0 / 0.55)"
        strokeWidth={3.5}
        strokeLinejoin="round"
      />
      <polygon
        points={pontos}
        fill="none"
        stroke="var(--color-brasa)"
        strokeWidth={1.75}
        strokeLinejoin="round"
      />
      <line
        x1={pos.meioDeCima.x}
        y1={pos.meioDeCima.y}
        x2={pos.girar.x}
        y2={pos.girar.y}
        stroke="var(--color-brasa)"
        strokeWidth={1.5}
      />
      {(["nw", "ne", "se", "sw"] as const).map((a) => (
        <rect
          key={a}
          x={pos.alcas[a].x - lado / 2}
          y={pos.alcas[a].y - lado / 2}
          width={lado}
          height={lado}
          rx={3}
          fill="white"
          stroke="var(--color-brasa)"
          strokeWidth={2}
          transform={`rotate(${caixa.rotacao} ${pos.alcas[a].x} ${pos.alcas[a].y})`}
        />
      ))}
      <circle
        cx={pos.girar.x}
        cy={pos.girar.y}
        r={9}
        fill="white"
        stroke="var(--color-brasa)"
        strokeWidth={2}
      />
      {/* O arco de "girar" dentro da alça. */}
      <path
        d={`M ${pos.girar.x - 3.5} ${pos.girar.y + 1} a 3.6 3.6 0 1 1 1.2 2.6`}
        fill="none"
        stroke="var(--color-brasa)"
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </svg>
  );
}

function BotaoDeIcone({
  rotulo,
  dica,
  desabilitado,
  aoClicar,
  children,
}: {
  rotulo: string;
  dica?: string;
  desabilitado?: boolean;
  aoClicar: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={dica ?? rotulo}
      disabled={desabilitado}
      onClick={aoClicar}
      className="inline-flex size-11 shrink-0 items-center justify-center rounded-peca text-texto-suave transition-colors hover:bg-superficie hover:text-texto disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/** Um botão da barra da camada selecionada: ícone e rótulo curto, num alvo de 44 px de altura. */
function Ferramenta({
  rotulo,
  desabilitado,
  aoClicar,
  children,
}: {
  rotulo: string;
  desabilitado?: boolean;
  aoClicar: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      disabled={desabilitado}
      onClick={aoClicar}
      className={cx(
        "flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-peca text-texto-suave transition-colors",
        "hover:bg-superficie hover:text-texto disabled:cursor-not-allowed disabled:opacity-30",
      )}
    >
      {children}
      <span className="text-[0.6875rem] leading-none">{rotulo}</span>
    </button>
  );
}

/**
 * "Aplicar em todas as fotos": leva as camadas desta foto para as outras da folha. Duas formas — **substituir**
 * (as outras ficam com as mesmas camadas, perdendo as que tinham) ou **acrescentar** (as daqui entram por cima das
 * que elas já têm) —, e o resultado diz quantas fotos receberam e quais não couberam.
 */
function AplicarEmTodas({
  fotosNaFolha,
  semCamadas,
  escolhendo,
  aoEscolher,
  aoAplicar,
  resultado,
  recusada,
  aoDesfazer,
}: {
  fotosNaFolha: number;
  semCamadas: boolean;
  escolhendo: boolean;
  aoEscolher: (escolhendo: boolean) => void;
  aoAplicar: (modo: "substituir" | "acrescentar") => void;
  resultado: ResultadoDaAplicacao | null;
  /** A aplicação que não coube nos tetos da folha: nada mudou, e é o porquê. */
  recusada: string | null;
  aoDesfazer: () => void;
}) {
  const destino = fotosNaFolha === 1 ? "a outra foto" : `as outras ${fotosNaFolha} fotos`;
  const nas = fotosNaFolha === 1 ? "na outra foto" : `nas outras ${fotosNaFolha} fotos`;

  return (
    <section
      aria-label="Aplicar em todas as fotos"
      className="flex flex-col gap-2 border-t border-borda pt-4"
    >
      <h3 className="text-[0.9375rem] font-medium text-texto">Repetir nas outras fotos</h3>
      {recusada && <RecusaDaAplicacao mensagem={recusada} />}
      {fotosNaFolha === 0 ? (
        <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
          Só há esta foto na folha. Quando houver outras, dá para levar estas camadas para elas de
          uma vez.
        </p>
      ) : (
        <>
          <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
            Copia as camadas desta foto para {destino} da folha. Depois dá para ajustar cada foto à
            parte: o enquadramento delas não muda.
          </p>
          {!escolhendo ? (
            <Botao tom="quieto" className="min-h-11 self-start" onClick={() => aoEscolher(true)}>
              Aplicar em todas as fotos
            </Botao>
          ) : (
            <div className="flex flex-col gap-2 rounded-peca border border-borda bg-superficie p-3">
              <p className="text-[0.875rem] text-texto">Como aplicar {nas}?</p>
              <Botao tom="brasa" className="min-h-11" onClick={() => aoAplicar("substituir")}>
                Substituir as camadas das outras
              </Botao>
              <p className="text-[0.75rem] leading-relaxed text-texto-fraco">
                {semCamadas
                  ? "Esta foto não tem camadas: as outras ficam sem nenhuma."
                  : "As outras ficam com as mesmas camadas desta. As que elas tinham saem."}
              </p>
              <Botao
                tom="quieto"
                className="min-h-11"
                disabled={semCamadas}
                onClick={() => aoAplicar("acrescentar")}
              >
                Acrescentar por cima das que já têm
              </Botao>
              <p className="text-[0.75rem] leading-relaxed text-texto-fraco">
                As camadas daqui entram na frente das que as outras já têm. Cada foto cabe até{" "}
                {MAX_CAMADAS_POR_CELULA}.
              </p>
              <Botao tom="fantasma" formato="nu" onClick={() => aoEscolher(false)}>
                Voltar
              </Botao>
            </div>
          )}
        </>
      )}

      {resultado && (
        <div
          role="status"
          className="flex flex-col gap-1.5 rounded-peca border border-borda bg-superficie p-3"
        >
          <p className="text-[0.875rem] leading-relaxed text-texto">
            {resultado.atualizadas === 0
              ? "Nenhuma foto recebeu as camadas."
              : resultado.atualizadas === 1
                ? "1 foto recebeu as camadas."
                : `${resultado.atualizadas} fotos receberam as camadas.`}{" "}
            Dá para ajustar cada foto depois.
          </p>
          {resultado.naoCouberam.length > 0 && (
            <p className="text-[0.8125rem] leading-relaxed text-descartar">
              Não couberam (passariam de {MAX_CAMADAS_POR_CELULA} camadas) e ficaram como estavam:{" "}
              {resultado.naoCouberam.join(", ")}.
            </p>
          )}
          <Botao tom="fantasma" formato="nu" className="self-start" onClick={aoDesfazer}>
            Desfazer a aplicação
          </Botao>
        </div>
      )}
    </section>
  );
}

/** O motivo de a aplicação não ter ocorrido: aparece junto do título da seção e entra na tela se estava rolada para fora. */
function RecusaDaAplicacao({ mensagem }: { mensagem: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "nearest" });
  }, [mensagem]);
  return (
    <p
      ref={ref}
      role="alert"
      className="rounded-peca border border-descartar/40 bg-descartar/10 p-3 text-[0.8125rem] leading-relaxed text-texto"
    >
      {mensagem}
    </p>
  );
}
